/**
 * Workspace settings form for admin workspace detail page.
 */

import { useState } from "react";
import { useAdminUpdateWorkspace } from "../../hooks/useAdminWorkspaces";
import { AlertCircle, Check, FolderKanban, Globe, Layers, Loader2 } from "lucide-react";
import type { AdminWorkspaceDetail } from "../../services/naraDoor";

interface WorkspaceSettingsFormProps {
  workspace: AdminWorkspaceDetail;
}

export function WorkspaceSettingsForm({ workspace }: WorkspaceSettingsFormProps) {
  // null = not being edited: the field follows the workspace, so a refetch shows.
  const [draftName, setDraftName] = useState<string | null>(null);
  const name = draftName ?? (workspace.name || "");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessSuccess, setAccessSuccess] = useState(false);

  const updateMutation = useAdminUpdateWorkspace();
  // Saved on click, so it is server data, not a draft.
  const tagAccess: string = (workspace as any).settings?.tags?.access || "custom";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Name is required");
      return;
    }

    try {
      await updateMutation.mutateAsync({
        workspaceId: workspace.id,
        name: trimmedName,
      });
      setDraftName(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || "Failed to update workspace");
    }
  };

  const handleTagAccessChange = async (newLevel: string) => {
    setAccessError(null);
    setAccessSuccess(false);
    setAccessSaving(true);

    try {
      // Merge with existing settings
      const currentSettings = (workspace as any).settings || {};
      const newSettings = {
        ...currentSettings,
        tags: { ...(currentSettings.tags || {}), access: newLevel },
      };

      await updateMutation.mutateAsync({
        workspaceId: workspace.id,
        settings: newSettings,
      });

      setAccessSuccess(true);
      setTimeout(() => setAccessSuccess(false), 3000);
    } catch (err: any) {
      setAccessError(err.message || "Failed to update tag access level");
    } finally {
      setAccessSaving(false);
    }
  };

  const hasChanges = name.trim() !== (workspace.name || "");

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
          <Check className="h-5 w-5 text-green-600 shrink-0" />
          <p className="text-sm text-green-800 dark:text-green-200">Settings updated successfully</p>
        </div>
      )}

      <div>
        <label
          htmlFor="workspaceName"
          className="block text-sm font-medium text-foreground mb-1"
        >
          Workspace Name
        </label>
        <input
          id="workspaceName"
          type="text"
          value={name}
          onChange={(e) => setDraftName(e.target.value)}
          placeholder="Enter workspace name"
          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          The display name for this workspace
        </p>
      </div>

      {/* Display current handle (read-only, edit in Danger Zone) */}
      {workspace.handle && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Handle
          </label>
          <div className="flex items-center">
            <span className="px-3 py-2 bg-muted border border-border rounded-lg text-muted-foreground">
              @{workspace.handle}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            To change the handle, go to the Danger Zone tab
          </p>
        </div>
      )}

      {/* Tag Access Settings */}
      <div className="pt-6 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground mb-1">
          Tag Access
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Controls tag visibility and management for this workspace.
        </p>

        {accessError && (
          <div className="flex items-center gap-3 p-3 mb-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-200">{accessError}</p>
          </div>
        )}

        {accessSuccess && (
          <div className="flex items-center gap-3 p-3 mb-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
            <Check className="h-4 w-4 text-green-600 shrink-0" />
            <p className="text-sm text-green-800 dark:text-green-200">Tag access level updated</p>
          </div>
        )}

        <div className="space-y-3">
          {[
            {
              value: "none",
              icon: FolderKanban,
              label: "Disabled",
              description: "Tags disabled. No cross-project sharing. Tag UI hidden for tenants.",
            },
            {
              value: "system",
              icon: Globe,
              label: "System Only",
              description: "Tenants see admin-created (locked) tags only. Can assign to projects but cannot create/edit/delete.",
            },
            {
              value: "custom",
              icon: Layers,
              label: "Custom Tags",
              description: "Tenants see admin tags (locked, can't edit) and can create/edit/delete their own tags.",
            },
          ].map((option) => {
            const Icon = option.icon;
            const isSelected = tagAccess === option.value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={accessSaving}
                onClick={() => handleTagAccessChange(option.value)}
                className={`w-full flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-colors ${
                  isSelected
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30"
                    : "border-border hover:border-blue-300 hover:bg-accent/50"
                } ${accessSaving ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${isSelected ? "text-blue-600" : "text-muted-foreground"}`} />
                <div>
                  <div className={`text-sm font-medium ${isSelected ? "text-blue-700 dark:text-blue-300" : "text-foreground"}`}>
                    {option.label}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {option.description}
                  </div>
                </div>
                {isSelected && (
                  <Check className="h-4 w-4 text-blue-600 shrink-0 ml-auto mt-0.5" />
                )}
              </button>
            );
          })}
        </div>

        {accessSaving && (
          <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Updating tag access level...
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-border">
        <button
          type="submit"
          disabled={!hasChanges || updateMutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save Changes
        </button>
      </div>
    </form>
  );
}
